import { makeAutoObservable } from "mobx";
import type { RootStore } from "stores/index";
import GLControlsService, { AssetGLNotification } from "../shared/GLControlsService";

/**
 * Asset Transaction record for display
 */
export interface AssetTransaction {
  id: string;
  transactionDate: string;
  transactionType: "invoice" | "bill" | "journal_entry" | "payment";
  transactionNumber: string;
  description: string;
  glAccountNumber: string;
  glAccountName: string;
  debitAmount: number;
  creditAmount: number;
  plantCode: string;
  jobId?: string;
  jobName?: string;
  vendorId?: string;
  vendorName?: string;
  customerId?: string;
  customerName?: string;
  createdBy: string;
  createdAt: string;
  hasAttachment: boolean;
  attachmentCount: number;
  isAcknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  notes?: string;
}

/**
 * Filter options for asset transactions
 */
export interface AssetTransactionFilters {
  dateFrom: string | null;
  dateTo: string | null;
  transactionType: AssetTransaction["transactionType"] | "all";
  plantCode: string | "all";
  acknowledged: "all" | "yes" | "no";
  searchQuery: string;
}

/**
 * AssetTransactions Store
 *
 * Manages the asset transactions view for CPA documentation
 * per Implementation Plan Phase 1: GL Controls & Asset Tracking (1.2)
 */
class AssetTransactionsStore {
  rootStore: RootStore;
  glControlsService: GLControlsService;

  // Data
  selectedTransactionId: string | null = null;

  // UI State
  error: string | null = null;

  // Filters
  filters: AssetTransactionFilters = {
    dateFrom: null,
    dateTo: null,
    transactionType: "all",
    plantCode: "all",
    acknowledged: "all",
    searchQuery: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.glControlsService = new GLControlsService(rootStore);
    makeAutoObservable(this, { rootStore: false, glControlsService: false });
  }

  // ==================== ServiceStore Access ====================

  get assetTransactionsServiceStore() {
    return this.rootStore.serviceStores?.["asset-transactions"];
  }

  get transactions(): AssetTransaction[] {
    return (this.assetTransactionsServiceStore?.entities as AssetTransaction[]) || [];
  }

  get loading(): boolean {
    return this.assetTransactionsServiceStore?.isFindPending || false;
  }

  // ==================== Computed Properties ====================

  /**
   * Get filtered transactions
   */
  get filteredTransactions(): AssetTransaction[] {
    return this.transactions.filter((txn) => {
      // Date range filter
      if (this.filters.dateFrom) {
        if (txn.transactionDate < this.filters.dateFrom) return false;
      }
      if (this.filters.dateTo) {
        if (txn.transactionDate > this.filters.dateTo) return false;
      }

      // Transaction type filter
      if (this.filters.transactionType !== "all") {
        if (txn.transactionType !== this.filters.transactionType) return false;
      }

      // Plant filter
      if (this.filters.plantCode !== "all") {
        if (txn.plantCode !== this.filters.plantCode) return false;
      }

      // Acknowledged filter
      if (this.filters.acknowledged !== "all") {
        const isAcknowledged = this.filters.acknowledged === "yes";
        if (txn.isAcknowledged !== isAcknowledged) return false;
      }

      // Search query
      if (this.filters.searchQuery) {
        const query = this.filters.searchQuery.toLowerCase();
        const matches =
          txn.transactionNumber.toLowerCase().includes(query) ||
          txn.description.toLowerCase().includes(query) ||
          txn.glAccountNumber.toLowerCase().includes(query) ||
          txn.glAccountName.toLowerCase().includes(query) ||
          txn.vendorName?.toLowerCase().includes(query) ||
          txn.customerName?.toLowerCase().includes(query);
        if (!matches) return false;
      }

      return true;
    });
  }

  /**
   * Get selected transaction
   */
  get selectedTransaction(): AssetTransaction | null {
    if (!this.selectedTransactionId) return null;
    return this.transactions.find((t) => t.id === this.selectedTransactionId) || null;
  }

  /**
   * Summary metrics
   */
  get metrics() {
    const filtered = this.filteredTransactions;
    return {
      totalCount: filtered.length,
      totalDebits: filtered.reduce((sum, t) => sum + t.debitAmount, 0),
      totalCredits: filtered.reduce((sum, t) => sum + t.creditAmount, 0),
      acknowledgedCount: filtered.filter((t) => t.isAcknowledged).length,
      pendingCount: filtered.filter((t) => !t.isAcknowledged).length,
      withAttachments: filtered.filter((t) => t.hasAttachment).length,
      missingAttachments: filtered.filter((t) => !t.hasAttachment).length,
      byType: {
        invoice: filtered.filter((t) => t.transactionType === "invoice").length,
        bill: filtered.filter((t) => t.transactionType === "bill").length,
        journal_entry: filtered.filter((t) => t.transactionType === "journal_entry").length,
        payment: filtered.filter((t) => t.transactionType === "payment").length,
      },
    };
  }

  /**
   * Get unique plants for filter
   */
  get uniquePlants(): string[] {
    return [...new Set(this.transactions.map((t) => t.plantCode))].sort();
  }

  // ==================== Actions ====================

  /**
   * Load asset transactions
   */
  async loadData(): Promise<void> {
    this.error = null;
    try {
      await this.assetTransactionsServiceStore?.find({ query: { $limit: 500 } });
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load asset transactions";
    }
  }

  /**
   * Set filter value
   */
  setFilter<K extends keyof AssetTransactionFilters>(
    key: K,
    value: AssetTransactionFilters[K]
  ): void {
    this.filters[key] = value;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      dateFrom: null,
      dateTo: null,
      transactionType: "all",
      plantCode: "all",
      acknowledged: "all",
      searchQuery: "",
    };
  }

  /**
   * Select a transaction
   */
  selectTransaction(transactionId: string | null): void {
    this.selectedTransactionId = transactionId;
  }

  /**
   * Acknowledge a transaction
   */
  async acknowledgeTransaction(transactionId: string, notes?: string): Promise<void> {
    const patchData: Record<string, unknown> = { isAcknowledged: true };
    if (notes) {
      patchData.notes = notes;
    }
    await this.assetTransactionsServiceStore?.patch(transactionId, patchData);
  }

  /**
   * Bulk acknowledge transactions
   */
  async bulkAcknowledge(transactionIds: string[], notes?: string): Promise<void> {
    await Promise.all(transactionIds.map((id) => this.acknowledgeTransaction(id, notes)));
  }

  /**
   * Export transactions for CPA documentation
   */
  exportForCPA(): string {
    const headers = [
      "Date",
      "Type",
      "Transaction #",
      "Description",
      "GL Account",
      "GL Name",
      "Debit",
      "Credit",
      "Plant",
      "Vendor/Customer",
      "Has Attachment",
      "Acknowledged",
      "Acknowledged By",
      "Notes",
    ].join(",");

    const rows = this.filteredTransactions.map((t) =>
      [
        t.transactionDate,
        t.transactionType,
        t.transactionNumber,
        `"${t.description.replace(/"/g, '""')}"`,
        t.glAccountNumber,
        t.glAccountName,
        t.debitAmount || "",
        t.creditAmount || "",
        t.plantCode,
        t.vendorName || t.customerName || "",
        t.hasAttachment ? "Yes" : "No",
        t.isAcknowledged ? "Yes" : "No",
        t.acknowledgedBy || "",
        `"${(t.notes || "").replace(/"/g, '""')}"`,
      ].join(",")
    );

    return [headers, ...rows].join("\n");
  }

  /**
   * Get transaction type label
   */
  getTransactionTypeLabel(type: AssetTransaction["transactionType"]): string {
    const labels: Record<AssetTransaction["transactionType"], string> = {
      invoice: "Invoice",
      bill: "Bill",
      journal_entry: "Journal Entry",
      payment: "Payment",
    };
    return labels[type];
  }

  /**
   * Get transaction type color
   */
  getTransactionTypeColor(
    type: AssetTransaction["transactionType"]
  ): "primary" | "secondary" | "success" | "warning" {
    const colors: Record<AssetTransaction["transactionType"], "primary" | "secondary" | "success" | "warning"> = {
      invoice: "primary",
      bill: "secondary",
      journal_entry: "success",
      payment: "warning",
    };
    return colors[type];
  }

}

export default AssetTransactionsStore;
